import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Input, Paginate } from '@cokitchen/cokitchen-components';
import { FilterAltIcon } from 'cokitchen-icons';
import { ENUM_STOCK_CATEGORY } from 'models/stock.model';
import { ALL, BELOW, ABOVE } from 'utils/constants';

export type CategoryListType = { text: string; value: ExtendedStockType };
export type ExtendedStockType = ENUM_STOCK_CATEGORY | typeof ALL;
export const EXPIRING_SOON = 'expiring-soon';
export const EXPIRED = 'expired';
export type InventoryQuantityType =
  | typeof ALL
  | typeof BELOW
  | typeof ABOVE
  | typeof EXPIRING_SOON
  | typeof EXPIRED;

type Props = {
  lastPage: number;
  onPageChange?: (page: number) => void;
  searchValue: string;
  setSearchValue: (item: string) => void;
  quantityFilter: InventoryQuantityType;
  setQuantityFilter: Dispatch<SetStateAction<InventoryQuantityType>>;
  quantityOptions?: SelectOption<InventoryQuantityType>[];
  quantityLabel?: string;
  showQuantityFilter?: boolean;
  showSearch?: boolean;
  showPagination?: boolean;
};

export type SelectOption<T extends string> = { text: string; value: T };

type SingleSelectDropdownProps<T extends string> = {
  label: string;
  items: SelectOption<T>[];
  selected: T;
  onSelect: (value: T) => void;
};

const SingleSelectDropdown = <T extends string>({
  label,
  items,
  selected,
  onSelect
}: SingleSelectDropdownProps<T>) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <div className='relative' ref={wrapperRef}>
      <button
        type='button'
        onClick={() => setOpen((prev) => !prev)}
        className='border border-[#E5E6E6] rounded-[6px] text-xs leading-[19px] h-[36px] flex px-3 justify-between items-center w-[120px] cursor-pointer'>
        <span className='text-[#5E6164] text-sm capitalize'>{label}</span>
        <FilterAltIcon className='text-[#5E6164]' />
      </button>
      {open && (
        <div className='absolute z-20 mt-1 w-[160px] rounded-[6px] border border-[#E5E6E6] bg-white py-1 shadow-sm'>
          {items.map(({ text, value }) => (
            <button
              key={value}
              type='button'
              onClick={() => {
                onSelect(value);
                setOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-xs capitalize hover:bg-[#F2F2F3] ${
                selected === value ? 'text-red-5' : ''
              }`}>
              {text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StockFilters = ({
  lastPage,
  onPageChange,
  searchValue,
  setSearchValue,
  quantityFilter,
  setQuantityFilter,
  quantityOptions,
  quantityLabel: quantityLabelOverride,
  showQuantityFilter = true,
  showSearch = true,
  showPagination = true
}: Props) => {
  const DEFAULT_QUANTITY_LIST: SelectOption<InventoryQuantityType>[] = [
    {
      text: 'All',
      value: 'ALL'
    },
    {
      text: 'Below min quantity',
      value: 'BELOW'
    },
    {
      text: 'Above min quantity',
      value: 'ABOVE'
    }
  ];
  const quantityList = quantityOptions ?? DEFAULT_QUANTITY_LIST;
  const baseQuantityLabel = quantityLabelOverride ?? 'Quantity';

  const quantityLabel =
    quantityFilter === ALL
      ? baseQuantityLabel
      : quantityList.find((item) => item.value === quantityFilter)?.text || baseQuantityLabel;

  return (
    <div className='inventory_stock_filter flex items-center gap-4 flex-wrap'>
      <div className='flex flex-wrap gap-3 items-center grow'>
        {showQuantityFilter && (
          <SingleSelectDropdown
            label={quantityLabel}
            items={quantityList}
            selected={quantityFilter}
            onSelect={(value) => {
              setQuantityFilter(value);
              onPageChange?.(1);
            }}
          />
        )}

        {showSearch && (
          <Input
            type='search'
            className='w-[90%] sm:w-[280px]'
            small
            inputClass='max-h-[36px] rounded-[6px]'
            placeholder='Search by name'
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value.toLocaleLowerCase());
              onPageChange?.(1);
            }}
          />
        )}
      </div>

      {showPagination && (
        <Paginate
          pageCount={lastPage}
          pageRangeDisplayed={2}
          onPageChange={(e) => {
            onPageChange?.(e.selected + 1);
          }}
        />
      )}
    </div>
  );
};

export default StockFilters;
